import { Injectable } from '@angular/core';
import moment from 'moment';
import tinycolor from 'tinycolor2';
import { StorageService } from '../storage/storage.service';
import { environment } from 'src/environments/environment';
import { FeatureProperty, FeatureType, GeometryType, NotificationTypes, ScheduleWindowIssue } from 'src/app/models/enums';
import { NotificationService } from '../notification/notification.service';
import { AnimationItem } from 'lottie-web';
import {
  FormFieldValue,
  IBase,
  IOrderedTerrainStop,
  IPageResponse,
  IPaginatedList,
  IServiceArea,
  ITextMatchPart,
} from 'src/app/models';
import { Select2Option } from 'ng-select2-component';
import { APP } from 'src/app/config/app';
import { images } from 'src/images';
import { objectToSnake } from 'ts-case-convert';
import { initialPaginationState } from 'src/app/store/initialisation/pagination';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  public urlsToCache = [];

  public storageService = new StorageService();

  public lowercaseRegExp = /[a-z]/;
  public uppercaseRegExp = /[A-Z]/;
  public digitsRegExp = /[0-9]/;
  public specialCharactersRegExp = /[\^$\*.\[\]{}()?\-"!@#%&\/\\,><':;|_~`]/;
  public lengthRegExp = /^.{8,}$/;
  public otherCharsRegExp = /[=+]/;

  private readonly allowedCharsRegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]).{8,}$/;

  constructor(private notificationService?: NotificationService) {}

  public getUrl() {
    if (environment.location === 'tst') {
      return 'https://tst.nemi.mobi/backoffice/';
    } else if (environment.location === 'sta') {
      return 'https://sta.nemi.mobi/backoffice/';
    } else if (environment.location === 'demo') {
      return 'https://demo.nemi.mobi/backoffice/';
    } else if (environment.location === 'prod') {
      return 'https://prod.nemi.mobi/backoffice/';
    } else if (environment.location === 'dev') {
      return 'http://localhost:8080/backoffice/';
    }
    return 'https://prod.nemi.mobi/backoffice/';
  }

  public async getLanguage() {
    return this.storageService.getLanguage();
  }

  public capitalize(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  public normalizeStopName(name?: string): string {
    return (name ?? '').trim().toLowerCase();
  }

  public splitTextMatches(text: string, term: string): ITextMatchPart[] {
    const foldedTerm = this.foldText(term.trim()).folded;
    if (!text || !foldedTerm) return [{ text: text ?? '', match: false }];
    const { folded, sourceStarts, sourceEnds } = this.foldText(text);
    const parts: ITextMatchPart[] = [];
    let cursor = 0;
    let matchIndex = 0;
    let matchStart = folded.indexOf(foldedTerm);
    while (matchStart !== -1) {
      const sourceStart = sourceStarts[matchStart];
      const sourceEnd = sourceEnds[matchStart + foldedTerm.length - 1];
      if (sourceStart > cursor) parts.push({ text: text.slice(cursor, sourceStart), match: false });
      parts.push({ text: text.slice(sourceStart, sourceEnd), match: true, matchIndex: matchIndex++ });
      cursor = sourceEnd;
      matchStart = folded.indexOf(foldedTerm, matchStart + foldedTerm.length);
    }
    if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false });
    return parts;
  }

  public textMatches(text: string | undefined, term: string): boolean {
    const foldedTerm = this.foldText(term.trim()).folded;
    return !foldedTerm || this.foldText(text ?? '').folded.includes(foldedTerm);
  }

  private foldText(text: string): { folded: string; sourceStarts: number[]; sourceEnds: number[] } {
    let folded = '';
    const sourceStarts: number[] = [];
    const sourceEnds: number[] = [];
    let sourceIndex = 0;
    for (const char of text) {
      const foldedChar = char.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      if (!foldedChar && sourceEnds.length) sourceEnds[sourceEnds.length - 1] = sourceIndex + char.length;
      for (const foldedUnit of foldedChar) {
        folded += foldedUnit;
        sourceStarts.push(sourceIndex);
        sourceEnds.push(sourceIndex + char.length);
      }
      sourceIndex += char.length;
    }
    return { folded, sourceStarts, sourceEnds };
  }

  public truncateCoordinate(value: number): number {
    return Math.trunc(value * 1e6) / 1e6;
  }

  public clampStopRadius(value: number): number {
    return Math.min(Math.max(Math.trunc(value), APP.MIN_STOP_RADIUS), APP.MAX_STOP_RADIUS);
  }

  public blockInvalidRadiusKeys(event: KeyboardEvent) {
    if (['-', '+', 'e', 'E', '.', ','].includes(event.key)) {
      event.preventDefault();
    }
  }

  public visibleTooltip(id: any) {
    const e = document.getElementById(id);
    if (!e) return false;
    return e.scrollWidth > e.clientWidth || e.scrollHeight > e.clientHeight;
  }

  public closeComponent(
    e: Event,
    inputs: HTMLElement[],
    dropdowns: HTMLElement[],
    title: string,
  ): boolean {
    const input = inputs.find(
      (input: HTMLElement) => input.id === 'input-' + title,
    );
    const dropdown = dropdowns.find(
      (dropdown: HTMLElement) => dropdown.id === 'dropdown-' + title,
    );

    if (input && dropdown) {
      const isClickInsideInput = input.contains(e.target as Node);
      const isClickInsideDropdown = dropdown.contains(e.target as Node);

      if (!isClickInsideInput && !isClickInsideDropdown) {
        return true;
      }
    }
    return false;
  }

  public closeComponentOnlyWithDropdown(
    e: Event,
    dropdownsParent: HTMLElement,
    dropdowns: HTMLElement[],
    title: string,
  ): boolean {
    const dropdown = dropdowns.find(
      (dropdown: HTMLElement) => dropdown.id === 'dropdown-' + title,
    );

    if (dropdown && dropdownsParent) {
      const isClickInsideDropdown = dropdown.contains(e.target as Node);
      const isClickInsideDropdownParent = dropdownsParent.contains(
        e.target as Node,
      );

      if (!isClickInsideDropdown && !isClickInsideDropdownParent) {
        return true;
      }
    }
    return false;
  }

  public openComponentOnlyWithDropdown(
    e: Event,
    dropdownsParent: HTMLElement,
    dropdowns: HTMLElement[],
    title: string,
  ): boolean {
    const dropdown = dropdowns.find(
      (dropdown: HTMLElement) => dropdown.id === 'dropdown-' + title,
    );

    if (dropdown && dropdownsParent) {
      const isClickInsideDropdown = dropdown.contains(e.target as Node);
      const isClickInsideDropdownParent = dropdownsParent.contains(
        e.target as Node,
      );

      if (isClickInsideDropdown || isClickInsideDropdownParent) {
        return true;
      }
    }
    return false;
  }

  public closeCustomModals(
    e: Event,
    modal: HTMLElement,
    componentOpen: HTMLElement,
  ): boolean {
    if (modal && componentOpen) {
      const isClickInsideModal = modal.contains(e.target as Node);
      const isClickInsideComponentOpen = componentOpen.contains(
        e.target as Node,
      );

      // Don't close forms when clicking on push notifications
      const isClickInsidePushNotifications = this.isClickInsideExcludedElements(e);

      if (!isClickInsideModal && !isClickInsideComponentOpen && !isClickInsidePushNotifications) {
        return true;
      }
    }
    return false;
  }

  public closeCustomModalsWithModals(
    e: Event,
    modal: HTMLElement,
    modals: Node[],
    componentOpen: HTMLElement,
  ): boolean {
    if (modal && componentOpen) {
      const isClickInsideModal = modal.contains(e.target as Node);
      const isClickInsideComponentOpen = componentOpen.contains(
        e.target as Node,
      );

      const isClickInsideModals = modals.some((modal: Node) => {
        return modal.contains(e.target as Node)
      });

      // Don't close forms when clicking on push notifications
      const isClickInsidePushNotifications = this.isClickInsideExcludedElements(e);

      if (
        !isClickInsideModal &&
        !isClickInsideComponentOpen &&
        !isClickInsideModals &&
        !isClickInsidePushNotifications
      ) {
        return true;
      }
    }
    return false;
  }

  private isClickInsideExcludedElements(e: Event): boolean {
    const pushNotificationsContainer = document.querySelector('.push-notifications-container');
    if (pushNotificationsContainer?.contains(e.target as Node)) {
      return true;
    }
    return false;
  }

  public isDark(hexcolor: string) {
    return hexcolor ? tinycolor(hexcolor).isDark() : false;
  }

  public async getSvgImageData(image: string) {
    return await fetch(image)
      .then((response) => response.text())
      .then((svg) => svg);
  }

  public compareArrays(arr1: any, arr2: any) {
    return arr1
      .filter(
        (item: any) =>
          !arr2.some((item2: any) => {
            if (typeof item === 'object')
              return Object.keys(item).every((k) => item[k] === item2[k]);
            return item === item2;
          }),
      )
      .concat(
        arr2 &&
          arr2.filter(
            (item2: any) =>
              !arr1.some((item: any) => {
                if (typeof item2 === 'object')
                  return Object.keys(item2).every((k) => item2[k] === item[k]);
                return item2 === item;
              }),
          ),
      );
  }

  public compareObjects(obj1: any, obj2: any): string[] {
    return Object.keys(obj1)
      .filter((key) => {
        if (obj1[key] && Array.isArray(obj1[key])) {
          return this.compareArrays(obj1[key], obj2[key]).length > 0;
        }
        if (obj1[key] && typeof obj1[key] === 'object') {
          return this.compareObjects(obj1[key], obj2[key]).length > 0;
        }
        return obj1 && obj2 && obj1[key] !== obj2[key];
      })
      .concat(
        obj2 && Object.keys(obj2).filter((key) => obj2[key] && !obj1[key]),
      );
  }

  public compareArrayOfObjects(a: any[], b: any[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((obj, i) => JSON.stringify(obj) === JSON.stringify(b[i]));
  }

  public getDifferencesGroupedByDay(a: any[], b: any[]) {
    const differences = [];

    const maxLength = Math.max(a.length, b.length);

    for (let i = 0; i < maxLength; i++) {
      const aValue = a[i];
      const bValue = b[i];

      if (JSON.stringify(aValue) !== JSON.stringify(bValue)) {
        if (aValue) {
          differences.push(aValue);
        }
      }
    }
    return differences;
  }

  public numberHasDecimals(number: number) {
    const result = number - Math.floor(number) !== 0;
    if (result) return true;
    return false;
  }

  public compare(propName: string) {
    const stripAccents = (value: any) => (value ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return function (a: any, b: any) {
      const valA = stripAccents(a[propName]);
      const valB = stripAccents(b[propName]);
      if (valA < valB) return -1;
      if (valA > valB) return 1;
      return 0;
    };
  }

  public convertDate(dateTime: any) {
    const value = Array.isArray(dateTime) ? this.toLocalDateTimeString(dateTime) : dateTime;
    const date = new Date(value).toString();
    let dateString = date.toString().replace(/GMT.*/g, '');
    dateString = dateString + ' UTC';
    return new Date(dateString);
  }

  public counter(i: number) {
    return new Array(i);
  }

  public isTextSelected() {
    return window.getSelection()!.toString().length > 0;
  }

  public toTrimmedText(value: FormFieldValue): string {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  public toOptionalText(value: FormFieldValue): string | null {
    return this.toTrimmedText(value) || null;
  }

  public toOptionalNumber(value: FormFieldValue): number | null {
    const text = this.toTrimmedText(value);
    return text === '' || Number.isNaN(Number(text)) ? null : Number(text);
  }

  public toSelectOptions(entities: IBase[]): Select2Option[] {
    return entities.map((entity) => ({ value: entity.id!, label: entity.name ?? '' }));
  }

  public toQueryParams(query: object): Record<string, string> {
    const queryParams: Record<string, string> = {};
    Object.entries(objectToSnake(query)).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      queryParams[key] = String(value);
    });
    return queryParams;
  }

  public getDataWithPagination<T>(page: IPageResponse<T>, actualPage: number): IPaginatedList<T> {
    return {
      value: page.content,
      pagination: {
        ...initialPaginationState,
        numberOfElements: page.numberOfElements,
        totalPages: page.totalPages,
        totalElements: page.totalElements,
        first: page.first,
        last: page.last,
        actualPage,
        size: page.size,
      },
    };
  }

  public getPaginationItems(
    currentPage: number,
    lastPage: number,
    maxLength: number,
  ) {
    const res: Array<number> = [];

    // handle lastPage less than maxLength
    if (lastPage <= maxLength) {
      for (let i = 1; i <= lastPage; i++) {
        res.push(i);
      }
    } else if (currentPage === 1) {
      for (let i = 1; i <= maxLength - 2; i++) {
        res.push(i);
      }
      res.push(NaN);
      res.push(lastPage);
      return res;
    } else if (currentPage === lastPage) {
      res.push(1);
      res.push(NaN);
      for (let i = lastPage - 2; i <= lastPage; i++) {
        res.push(i);
      }
      return res;
    }
    // handle ellipsis logics
    else {
      const firstPage = 1;
      const confirmedPagesCount = 3;
      const deductedMaxLength = maxLength - confirmedPagesCount;
      const sideLength = deductedMaxLength / 2;

      // handle ellipsis in the middle
      if (
        currentPage - firstPage < sideLength ||
        lastPage - currentPage < sideLength
      ) {
        for (let j = 1; j <= sideLength + firstPage; j++) {
          res.push(j);
        }

        res.push(NaN);

        for (let k = lastPage - sideLength; k <= lastPage; k++) {
          res.push(k);
        }
      }
      // handle two ellipsis
      else if (
        currentPage - firstPage >= deductedMaxLength &&
        lastPage - currentPage >= deductedMaxLength
      ) {
        const deductedSideLength = sideLength - 1;

        res.push(1);
        res.push(NaN);

        for (
          let l = currentPage - deductedSideLength;
          l <= currentPage + deductedSideLength;
          l++
        ) {
          res.push(l);
        }

        res.push(NaN);
        res.push(lastPage);
      }
      // handle ellipsis not in the middle
      else {
        const isNearFirstPage =
          currentPage - firstPage < lastPage - currentPage;
        let remainingLength = maxLength;

        if (isNearFirstPage) {
          for (let m = 1; m <= currentPage + 1; m++) {
            res.push(m);
            remainingLength -= 1;
          }

          res.push(NaN);
          remainingLength -= 1;

          for (let n = lastPage - (remainingLength - 1); n <= lastPage; n++) {
            res.push(n);
          }
        } else {
          for (let o = lastPage; o >= currentPage - 1; o--) {
            res.unshift(o);
            remainingLength -= 1;
          }

          res.unshift(NaN);
          remainingLength -= 1;

          for (let p = remainingLength; p >= 1; p--) {
            res.unshift(p);
          }
        }
      }
    }
    return res;
  }
  
  // Anchors with a click binding lose their default action on touch devices (PointerClickPlugin), so the tab is opened by hand
  public openInNewTab(event: Event, url: string): void {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank', 'noopener');
  }

  public isLate(delay?: number | null): boolean {
    return (delay ?? 0) > 0;
  }

  public displayedDelay(delay?: number | null): number {
    return -(delay ?? 0);
  }
  
  public isUrlUnderPath(url: string, path: string): boolean {
    const urlPath = url.split(/[?#]/)[0];
    return urlPath === path || urlPath.startsWith(`${path}/`);
  }

  public isTouchDevice() {
    const userAgent = navigator.userAgent.toLowerCase();
    return ('ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0) || (/(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent));
  }

  public minutesToTime(minutes: number): string {
    const normalized = ((minutes % APP.MINUTES_PER_DAY) + APP.MINUTES_PER_DAY) % APP.MINUTES_PER_DAY;
    const hours = Math.floor(normalized / APP.MINUTES_PER_HOUR).toString().padStart(2, '0');
    const mins = (normalized % APP.MINUTES_PER_HOUR).toString().padStart(2, '0');
    return `${hours}:${mins}`;
  }

  // An end before the start finishes the next day, as the backend generates the departures
  public scheduleWindowMinutes(startTime?: string, endTime?: string): number {
    const minutes = this.timeOfDayMinutes(endTime) - this.timeOfDayMinutes(startTime);
    return minutes < 0 ? minutes + APP.MINUTES_PER_DAY : minutes;
  }

  public scheduleWindowIssue(
    row: { startTime?: string; endTime?: string },
    maxPathTime?: number,
    minPathTime?: number,
  ): ScheduleWindowIssue | null {
    const minutes = this.scheduleWindowMinutes(row.startTime, row.endTime);
    if (minPathTime && minutes < minPathTime) return ScheduleWindowIssue.BELOW_MIN_PATH_TIME;
    if (maxPathTime && minutes < maxPathTime) return ScheduleWindowIssue.BELOW_MAX_PATH_TIME;
    return null;
  }

  private timeOfDayMinutes(time?: string): number {
    const [hours, minutes] = String(time ?? '00:00').split(':').map(Number);
    return (hours || 0) * APP.MINUTES_PER_HOUR + (minutes || 0);
  }

  public getUserLabel(user: any) {
    // Drivers carry a license number where regular users carry a dni
    const extraInfo =
    user &&
    ((user.licenseNumber && user.licenseNumber) ||
    (user.dni && user.dni) ||
    (user.phone && user.phone) ||
    (user.email && user.email));
    return user.firstName === null && user.lastName === null
          ? `${user.email}`
          : `${user.firstName} ${user.lastName} (${extraInfo})`;
  }

  public isJSON(value: string): boolean {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }

  downloadDocument(response: ArrayBuffer, filename: string, extension: string) {
    const blob = new Blob([response], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);

    const doc = document.createElement('a');
    doc.href = url;
    doc.download = `${filename}.${extension}`;
    document.body.appendChild(doc);
    doc.click();

    window.URL.revokeObjectURL(url);
  }

  downloadBlob(content: BlobPart, filename: string, mime: string) {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
    const url = window.URL.createObjectURL(blob);

    const doc = document.createElement('a');
    doc.href = url;
    doc.download = filename;
    document.body.appendChild(doc);
    doc.click();
    doc.remove();

    window.URL.revokeObjectURL(url);
  }

  readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  showSuccessNotification(actionKey: string) {
    this.showNotification(images.notifications.check, actionKey, NotificationTypes.SUCCESS);
  }

  showNotification(img: string, actionKey: string, type: NotificationTypes, specificTitle?: string, specificText?: string, extraText?: string, translate?: boolean) {
    this.notificationService!.image = img;
    this.notificationService!.title = specificTitle ? `${actionKey}.${specificTitle}` : `${actionKey}.title`;
    this.notificationService!.message = specificText ? `${actionKey}.${specificText}` : `${actionKey}.text`;
    this.notificationService!.extraText = extraText || '';
    this.notificationService!.translate = translate || true;
    if (!translate && actionKey === '' && specificTitle && specificText) {
      this.notificationService!.title = specificTitle;
      this.notificationService!.message = specificText;
    }
    this.notificationService!.show(type);
  }

  formatSecondsAsTime(seconds?: number): string {
    if (!seconds) return '0';
    const parts = this.convertSecondsToMinutesAndSeconds(seconds).split(':');
    const mins = Number(parts[0]);
    const secs = Number(parts[1]);
    return secs > 0 ? `${mins} min ${secs} s` : `${mins} min`;
  }

  convertSecondsToMinutesAndSeconds(value: number = 0, ignoreNulls: boolean = true) {
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;

    const formattedMinutes = String(minutes);
    const formattedSeconds = String(seconds).padStart(2, '0');

    if (formattedSeconds === '00' && ignoreNulls) {
      return `${formattedMinutes}`;
    }
    return `${formattedMinutes}:${formattedSeconds}`;
  }

  convertMinutesAndSecondsToSeconds(value: string): number {
    const [minutes, seconds] = value.split(':').map(Number);
    return minutes * 60 + seconds;
  }

  animationCreated(animationItem: AnimationItem, time: number): void {
    animationItem.setSpeed(time);
  }

  getFiltersCount(filters: any) {
    const keys = Object.keys(filters);
    return keys.reduce((count, key) => {
      let number = 0;
      if (Array.isArray(filters[key])) number = filters[key].length > 0 ? 1 : 0;
      else if (typeof filters[key] === 'string')
        number = filters[key] === '' ? 0 : 1;
      else number = filters[key] ? 1 : 0;
      return count + number;
    }, 0);
  }

  getArraysIntersection(arr1: any[], arr2: any[]) {
    return arr1.filter((value) => arr2.includes(value));
  }

  sanitizeObject(object: any) {
    const keys = Object.keys(object);
    keys.map((key: string) => {
      if (!object[key] || object[key] === undefined) object[key] = "";
    });
    return object;
  }

  // Zone-less town wall clock: the reference the API stores for availabilities and alterations.
  toLocalDateTimeString(value: any): any {
    if (!value) return value;
    const parsed = Array.isArray(value)
      ? moment([value[0], value[1] - 1, value[2], value[3] ?? 0, value[4] ?? 0, value[5] ?? 0])
      : moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm:ss') : value;
  }

  convertDateTimeArrays(data: any): any {
    if (Array.isArray(data)) {
      return data.map(item => this.convertDateTimeArrays(item));
    }

    if (data && typeof data === "object") {
      const newObj: any = {};
      for (const key of Object.keys(data)) {
        const value = data[key];
        if (/datetime/i.test(key) && Array.isArray(value)) {
          const [year, month, day, hour, minute, second = 0] = value;
          const date = new Date(year, month - 1, day, hour, minute, second);
          newObj[key] = date.toISOString();
        } else {
          newObj[key] = this.convertDateTimeArrays(value);
        }
      }
      return newObj;
    }
    return data;
  }

  // isCharValid(character: string): boolean {
  //   return (
  //     this.lowercaseRegExp.test(character) ||
  //     this.uppercaseRegExp.test(character) ||
  //     this.digitsRegExp.test(character) ||
  //     this.specialCharactersRegExp.test(character) ||
  //     this.otherCharsRegExp.test(character)
  //   )
  // }

  // isPasswordValid(password: string): boolean {
  //   let isValid = true

  //   if (!this.lowercaseRegExp.test(password)) isValid = false
  //   if (!this.uppercaseRegExp.test(password)) isValid = false
  //   if (!this.digitsRegExp.test(password)) isValid = false
  //   if (!this.specialCharactersRegExp.test(password)) isValid = false
  //   if (!this.lengthRegExp.test(password)) isValid = false
  //   if (!Array.from(password).every(this.isCharValid)) isValid = false

  //   return isValid
  // }

  isPasswordValid(password: string): boolean {
    return this.allowedCharsRegExp.test(password);
  }

  findFeatureByPropertyId(mapData: google.maps.Data, id: any, type: FeatureType): google.maps.Data.Feature | undefined {
    let found: google.maps.Data.Feature | undefined;
    mapData.forEach((feature) => {
      if (feature.getProperty('id') === id && feature.getProperty('type') === type) found = feature;
    });
    return found;
  }

  // One collection for everything: route features plus every town stop as a plain stop with its shared fields only
  cleanTerrainObject(terrain: any) {
    const sharedStopFeatures = new Set<any>();
    terrain.features = (terrain.features ?? [])
      .filter((feature: any) => feature.properties?.type !== FeatureType.OUTLINE)
      .map((feature: any) => {
        if (feature.properties?.type !== FeatureType.TOWN_STOP) return feature;
        const sharedStopFeature = this.toSharedStopFeature(feature);
        sharedStopFeatures.add(sharedStopFeature);
        return sharedStopFeature;
      });
    terrain.features = this.syncTerrainSegmentEndpoints(terrain).features
      .map((feature: any) => {
        delete feature.id;
        if (sharedStopFeatures.has(feature)) return feature;
        if (feature.properties?.type === FeatureType.STOP) return this.cleanStopFeature(feature);
        if (feature.properties?.type === FeatureType.SEGMENT) return this.cleanSegmentFeature(feature);
        return feature;
      });
    return terrain;
  }

  snakeCaseBody<T extends object>(body: T) {
    return this.restoreLegacyTerrainKeys(objectToSnake(body));
  }

  private restoreLegacyTerrainKeys<T>(value: T): T {
    if (Array.isArray(value)) return value.map((item) => this.restoreLegacyTerrainKeys(item)) as T;
    if (!value || typeof value !== 'object' || value instanceof Date) return value;
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [APP.LEGACY_TERRAIN_KEYS[key] ?? key, this.restoreLegacyTerrainKeys(item)])
    ) as T;
  }

  toSharedStopFeature(feature: any) {
    const stop = feature.properties ?? {};
    const properties: any = { id: stop.id, type: FeatureType.STOP, name: stop.name, code: stop.code || '' };
    APP.TOWN_STOP_METADATA_PROPERTIES.forEach((property) => { if (stop[property] != null) properties[property] = stop[property]; });
    if (stop.merge) properties.merge = this.cleanStopFeature(structuredClone(stop.merge));
    if (stop.bidirectional) properties.bidirectional = this.cleanStopFeature(structuredClone(stop.bidirectional));
    return { type: 'Feature', geometry: feature.geometry, properties };
  }

  syncTerrainSegmentEndpoints(terrain: any) {
    if (!terrain?.features) return terrain;

    const stopFeatures = new Map<any, any>();
    terrain.features.forEach((feature: any) => {
      if (feature.properties?.type === FeatureType.STOP && feature.properties.id != null) {
        stopFeatures.set(feature.properties.id, feature);
      }
    });
    if (stopFeatures.size === 0) return terrain;

    const features = terrain.features.map((feature: any) => {
      if (feature.properties?.type !== FeatureType.SEGMENT) return feature;
      const start = this.rebuildSegmentEndpoint(feature.properties.start, stopFeatures);
      const end = this.rebuildSegmentEndpoint(feature.properties.end, stopFeatures);
      if (start === feature.properties.start && end === feature.properties.end) return feature;
      return { ...feature, properties: { ...feature.properties, start, end } };
    });
    return { ...terrain, features };
  }

  // Walks the terrain from its start stop along the segment chain; without a chain the stops keep their order
  orderedTerrainStops(terrain: any): IOrderedTerrainStop[] {
    const features: any[] = terrain?.features ?? [];
    const stops = features.filter((feature) => feature.properties?.type === FeatureType.STOP);
    const endpointId = (endpoint: any) => Number(endpoint?.properties?.id ?? endpoint?.id);
    const stopById = new Map(stops.map((stop) => [Number(stop.properties.id), stop]));
    const segmentByStartId = new Map(
      features
        .filter((feature) => feature.properties?.type === FeatureType.SEGMENT)
        .map((segment) => [endpointId(segment.properties?.start), segment]),
    );
    const ordered: { stop: any; secondsFromStart: number }[] = [];
    const visited = new Set<number>();
    let secondsFromStart = 0;
    let current = stops.find((stop) => stop.properties?.start) ?? stops[0];
    while (current && !visited.has(Number(current.properties.id))) {
      visited.add(Number(current.properties.id));
      ordered.push({ stop: current, secondsFromStart });
      const segment = segmentByStartId.get(Number(current.properties.id));
      secondsFromStart += Number(segment?.properties?.weight ?? 0);
      current = segment ? stopById.get(endpointId(segment.properties?.end)) : undefined;
    }
    const walked = ordered.length >= 2 ? ordered : stops.map((stop) => ({ stop, secondsFromStart: 0 }));
    return walked.map(({ stop, secondsFromStart }) => ({
      id: Number(stop.properties.id),
      name: stop.properties.name,
      latitude: stop.geometry.coordinates[1],
      longitude: stop.geometry.coordinates[0],
      secondsFromStart,
      ...(stop.properties.inflexion ? { inflexion: true } : {}),
    }));
  }

  // Endpoints whose stop is not in the terrain, such as town stops, keep the copy they had.
  private rebuildSegmentEndpoint(endpoint: any, stopFeatures: Map<any, any>) {
    const stopFeature = stopFeatures.get(endpoint?.properties?.id);
    if (!endpoint || !stopFeature) return endpoint;
    return {
      ...endpoint,
      properties: structuredClone(stopFeature.properties),
      geometry: stopFeature.geometry ? structuredClone(stopFeature.geometry) : endpoint.geometry,
    };
  }

  closeOutlineRings(geometry: any): void {
    this.getPolygonsCoordinates(geometry).forEach((rings) =>
      rings.forEach((ring) => {
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (ring.length > 2 && (first[0] !== last[0] || first[1] !== last[1])) ring.push(first);
      }),
    );
  }

  areaToFeature(area: IServiceArea, featureId: string, terrainId?: number | string, color?: string) {
    const geometry = structuredClone(area.perimeter);
    this.closeOutlineRings(geometry);
    return {
      type: 'Feature',
      geometry,
      properties: {
        [FeatureProperty.TYPE]: FeatureType.OUTLINE,
        [FeatureProperty.ID]: featureId,
        [FeatureProperty.AREA_ID]: area.id ?? null,
        [FeatureProperty.NAME]: area.name,
        terrainId,
        ...(color ? { [FeatureProperty.OUTLINE_COLOR]: color } : {}),
      },
    };
  }

  featuresToAreas(features: any[]): IServiceArea[] {
    return (features ?? [])
      .filter((feature) => feature.properties?.[FeatureProperty.TYPE] === FeatureType.OUTLINE)
      .flatMap((feature) =>
        this.getPolygonsCoordinates(feature.geometry).map((coordinates, index) => ({
          id: index === 0 ? feature.properties?.[FeatureProperty.AREA_ID] ?? null : null,
          name: feature.properties?.[FeatureProperty.NAME] ?? '',
          perimeter: { type: GeometryType.POLYGON, coordinates },
        })),
      );
  }

  getPolygonsCoordinates(geometry: any): number[][][][] {
    if (geometry?.type === GeometryType.POLYGON) return geometry.coordinates?.length ? [geometry.coordinates] : [];
    if (geometry?.type === GeometryType.MULTI_POLYGON) return geometry.coordinates ?? [];
    return [];
  }

  cleanOutlineObject(outline: any) {
    if (!outline || !outline.geometry) return null;
    delete outline.properties?.[FeatureProperty.OUTLINE_COLOR];
    delete outline.properties?.terrainId;
    delete outline.properties?.type;
    delete outline.id;
    return outline;
  }

  cleanStopFeature(feature: any) {
    if (feature === '' || !feature) return null;
    if (!feature.type && feature.geometry && feature.properties) feature.type = 'Feature';
    if (!feature.properties) {
      feature.properties = {};
    } else {
      if (feature.properties.bidirectional == null) {
        delete feature.properties.bidirectional;
      } else {
        feature.properties.bidirectional = this.cleanStopFeature(feature.properties.bidirectional);
      }
      if (feature.properties.merge !== null) {
        feature.properties.merge = this.cleanStopFeature(feature.properties.merge);
      } else {
        delete feature.properties.merge;
      }
      if (feature.properties.alternatives?.length) {
        feature.properties.alternatives.forEach((alternative: any) => {
          delete alternative.id;
          if (alternative.properties?.bidirectional == null) delete alternative.properties.bidirectional;
          if (alternative.properties?.alternatives == null) delete alternative.properties.alternatives;
        });
      } else {
        delete feature.properties.alternatives;
      }
      if (feature.properties.connections) delete feature.properties.connections;
      if (feature.properties.lat) delete feature.properties.lat;
      if (feature.properties.lng) delete feature.properties.lng;
    }
    return feature;
  }

  cleanSegmentFeature(feature: any) {
    if (typeof feature.properties.weight === "number") feature.properties.weight = feature.properties.weight.toString();
    if (feature.properties.formattedTime) delete feature.properties.formattedTime;
    if (feature.properties.polyline) delete feature.properties.polyline;
    if (feature.properties.start) feature.properties.start = this.cleanStopFeature(feature.properties.start);
    if (feature.properties.end) feature.properties.end = this.cleanStopFeature(feature.properties.end);
    if (feature.properties.terrainColor) delete feature.properties.terrainColor;
    return feature;
  }

  positionMenuWithinViewport(
    menuBox: HTMLElement,
    clientX: number,
    clientY: number,
    topMargin = 80,
    leftMargin = 80,
    rightMargin = 40,
    bottomMargin = 40,
  ): void {
    const maxLeft = Math.max(window.innerWidth - menuBox.offsetWidth - rightMargin, leftMargin);
    const maxTop = Math.max(window.innerHeight - menuBox.offsetHeight - bottomMargin, topMargin);
    menuBox.style.left = Math.min(Math.max(clientX, leftMargin), maxLeft) + 'px';
    menuBox.style.top = Math.min(Math.max(clientY, topMargin), maxTop) + 'px';
  }

  isMac(): boolean {
    const platform = (navigator as any).userAgentData?.platform || navigator.platform || navigator.userAgent;
    return /mac|iphone|ipad|ipod/i.test(platform);
  }

  getShortcutText(letter: string, alt = false, shift = false): string {
    const key = letter.toUpperCase();
    if (this.isMac()) {
      return `${alt ? '⌥ ' : ''}${shift ? '⇧ ' : ''}${key}`;
    }
    const modifiers = [...(alt ? ['Alt'] : []), ...(shift ? ['Shift'] : [])];
    return `${modifiers.join('+')}+${key}`;
  }
}
